import React, { useEffect, useMemo, useState } from 'react'

import Heading from 'presentation/shared/components/Heading'
import { Patient } from 'domain/entities/patient-model'
import { AddSurgicalOrderFormValues } from '..'
import * as S from './styles'
import Button from 'presentation/shared/components/Button'
import ActualPage from 'presentation/shared/components/ActualPage'
import { useFormikContext } from 'formik'
import TextArea from 'presentation/shared/components/TextArea'
import { Profile } from 'common/enum/profile'
import getAgeByBirthdayDate from 'common/utils/getAgeByBirthdayDate'
import getDateFormattedByLocale from 'common/utils/getDateFormmatedByLocale'
import { DateLocales } from 'common/enum/date-locales'
import { isIsoDate } from 'common/utils/verify-iso-date'
import DocumentsArea from 'presentation/shared/components/DocumentsArea'
import { DocumentItem } from 'presentation/shared/components/DocumentsArea/DocumentsAreaModal'
import { LoadSurgicalOrderDocument } from 'domain/usecases/surgical-order/load-surgical-order-document'
import moment from 'moment-timezone'
import { documentList } from 'presentation/shared/pages/SurgeryDetails/documents'
import EditPatientDataModal, {
  ModifiedPatientData
} from 'presentation/secretary/components/EditPatientDataModal'
import { SurgicalStatusModel } from 'domain/entities/surgical-status-model'
import EditInsuranceModal, {
  ModifiedInsuranceData
} from 'presentation/secretary/components/EditInsuranceModal'
import { UpdateSurgicalOrderRegenerateGuides } from 'domain/usecases/surgical-order/update-surgical-order-regenerate-guides'
import SuccessModal from 'presentation/shared/components/Modal'
import { HealthInsurancePlans } from 'domain/usecases/health-insurance/load-health-insurance-plans'
import { toast } from 'react-toastify'
import { LoadHealthInsurancePlans } from 'domain/usecases/health-insurance/load-health-insurance-plans'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { useStores } from 'presentation/hooks/use-stores'
import { PatientDocument } from 'common/enum/patient-document'
import { conformToMask } from 'react-text-mask'
import { cpfMask } from 'presentation/utils/masks'
import { SurgicalOrderStatus } from 'presentation/shared/components/Timeline/status'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import { categorizeOpmes } from 'presentation/utils/categorize-opmes'

type Props = {
  patient?: Patient
  formValues?: AddSurgicalOrderFormValues
  opme?: {
    description?: string
    quantity?: string
  }[]
  procedure?: {
    tuss_id?: string
    description?: string
    quantity?: string
  }[]
  goBack?: () => void
  onlyVisualization?: boolean
  profile?: Profile
  surgical_order_id?: number
  surgicalOrder?: SurgicalStatusModel[]
  loadSurgicalOrderDocument?: LoadSurgicalOrderDocument
  updateSurgicalOrderRegenerateGuides?: UpdateSurgicalOrderRegenerateGuides
  loadHealthInsurance?: LoadHealthInsurancePlans
  isUploading?: boolean
} & WithLoadingProps

function SurgicalOrderReview({
  goBack,
  onlyVisualization,
  surgical_order_id,
  loadSurgicalOrderDocument,
  surgicalOrder,
  updateSurgicalOrderRegenerateGuides,
  loadHealthInsurance,
  setIsLoading,
  isUploading: isUploadingProp
}: Props) {
  const profile = useStores().currentAccount.getCurrentAccount().user.role
  const { values, submitForm, isSubmitting } =
    useFormikContext<AddSurgicalOrderFormValues>()
  const categorized = useMemo(
    () => categorizeOpmes(values.opmes || []),
    [values.opmes]
  )
  const [documents, setDocuments] = useState([] as DocumentItem[])
  const [patientEditModal, setPatientEditModal] = useState(false)
  const [insuranceEditModal, setInsuranceEditModal] = useState(false)
  const [handleSuccessModal, setHandleSuccessModal] = useState(false)
  const [insurances, setInsurances] = useState<HealthInsurancePlans[]>([])
  const HOSPITAL_SALVADOR_ID = 8
  const HOSPITAL_EMEC_ID = 22

  const getTechnique = () => {
    switch (values?.technique) {
      case 'conventional':
        return 'Convencional'
      case 'video':
        return 'Vídeo'
      case 'robotic':
        return 'Robótica'
      case 'makoRobot':
        return 'Robô Mako'
      case 'rosaRobot':
        return 'Robô Rosa'
      case 'laser':
        return 'Laser'
      default:
        return 'Não informado'
    }
  }

  const surgicalOrderStatusCanEditList = [
    SurgicalOrderStatus.PRE_SCHEDULE,
    SurgicalOrderStatus.OPME_QUOTATION,
    SurgicalOrderStatus.CRMO,
    SurgicalOrderStatus.HEALTH_INSURANCE_APPROVE,
    SurgicalOrderStatus.REVIEW,
    SurgicalOrderStatus.IN_HEALTH_INSURANCE,
    SurgicalOrderStatus.HEALTH_INSURANCE_APPROVE,
    SurgicalOrderStatus.CRMO_VALIDATION
  ]

  const statusCanEdit = () => {
    if (!surgicalOrder) return false
    const activeStatus = surgicalOrder.find((status) => status?.isActive)
    if (!activeStatus) return false
    const canEdit = surgicalOrderStatusCanEditList.includes(activeStatus.status)
    return canEdit
  }

  const getCharacter = () => {
    switch (values?.hospitalizationType) {
      case 'elective':
        return 'Eletivo'
      case 'urgency':
        return 'Urgência'
    }
  }

  const getHospitalRegime = () => {
    switch (values?.hospitalizationMode) {
      case 'hospital':
        return 'Hospitalar'
      case 'day hospital':
        return 'Hospital dia'
      case 'emergency room':
        return 'Pronto-socorro'
      case 'external':
        return 'Externo'
      case 'interned':
        return 'Internado'
      case 'ambulatory':
        return 'Ambulatorial'
    }
  }

  const handleDownloadDocumentFile = async (
    document_id: number
  ): Promise<File> => {
    if (surgical_order_id) {
      const surgicalOrderDocument = await loadSurgicalOrderDocument?.load(
        Number(surgical_order_id),
        document_id
      )
      if (surgicalOrderDocument) {
        return new File([surgicalOrderDocument?.data], '', {
          type: surgicalOrderDocument.contentType
        })
      }
    }
    return new File([''], '')
  }

  useEffect(() => {
    setDocuments(values?.documents_attached)
  }, [values?.documents_attached])

  const getExpectedDate = () => {
    if (isIsoDate(values.expectedDate))
      return moment.utc(values.expectedDate).format('DD/MM/YYYY')
    const momentExpectedDate = moment(values.expectedDate, 'YYYY-MM-DD')
    if (momentExpectedDate.isValid())
      return momentExpectedDate.format('DD/MM/YYYY')
    return values.expectedDate
  }

  const handleSubmit = async () => {
    if (surgical_order_id) {
      try {
        setIsLoading(true)
        await updateSurgicalOrderRegenerateGuides?.update({
          surgical_order_id: surgical_order_id,
          patient: values.patient,
          healthInsurance: values.healthInsurance,
          opme: {
            solicitations: values.opmes,
            providers: values.providers
          }
        })
        setHandleSuccessModal(true)
      } catch (error: any) {
        toast.error(error.message)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handlePatientData = (modifiedPatientData: ModifiedPatientData) => {
    setPatientEditModal(false)
    setHandleSuccessModal(!handleSuccessModal)
    values.patient.name = modifiedPatientData.name
    values.patient.birthday = modifiedPatientData.birthday
    values.patient.email = modifiedPatientData.email
    values.patient.gender = modifiedPatientData.gender
    values.patient.maritalStatus = modifiedPatientData.maritalStatus
    values.patient.phone = modifiedPatientData.phone
    values.patient.landlinePhone = modifiedPatientData.landlinePhone
  }

  const loadHealthInsurances = async (hospital_id: number) => {
    try {
      const insurances = await loadHealthInsurance?.load({
        hospital_id: hospital_id ?? 0,
        params: [
          'code',
          'description',
          'ansRegister',
          'plans {code, description, subPlan{code, description}}'
        ]
      })
      setInsurances(insurances ?? [])
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  useEffect(() => {
    async function loadData() {
      try {
        await loadHealthInsurances(values.hospital.hospital_id)
      } catch (error: any) {
        toast.error(error.message)
      }
    }

    loadData()
  }, [])

  const handleInsuranceData = (
    modifiedInsuranceData: ModifiedInsuranceData
  ) => {
    setInsuranceEditModal(false)
    setHandleSuccessModal(!handleSuccessModal)
    values.healthInsurance.healthInsuranceName =
      modifiedInsuranceData.healthInsuranceName
    values.healthInsurance.healthInsuranceCode =
      modifiedInsuranceData.healthInsuranceCode
    values.healthInsurance.company = modifiedInsuranceData.company
    values.healthInsurance.healthCard = modifiedInsuranceData.healthCard
    values.healthInsurance.healthPlanCode = modifiedInsuranceData.healthPlanCode
    values.healthInsurance.healthPlanName = modifiedInsuranceData.healthPlanName
    values.healthInsurance.healthSubPlanName =
      modifiedInsuranceData.healthSubPlanName
    values.healthInsurance.healthSubPlanCode =
      modifiedInsuranceData.healthSubPlanCode
    values.healthInsurance.validThru = modifiedInsuranceData.validThru
  }

  const formattedCpf =
    values.patient?.cpf &&
    conformToMask(values.patient?.cpf, cpfMask)?.conformedValue

  return (
    <S.Wrapper>
      <ActualPage
        text={onlyVisualization ? 'Voltar' : 'Editar'}
        onClick={goBack}
      />
      <Heading size="xlarge" color="primary" as="h1">
        Resumo do planejamento cirúrgico
      </Heading>
      {(values.createdAt || values.expectedDate) && (
        <S.DataOverview>
          {values.createdAt && (
            <div>
              <h6>Data de Criação: </h6>
              <span data-testid="created-at-span">
                {isIsoDate(values.createdAt)
                  ? moment.utc(values.createdAt).format('DD/MM/YYYY HH:mm')
                  : values.createdAt}
              </span>
            </div>
          )}
          {values.expectedDate && (
            <div>
              <h6>
                {values.hospitalizationType === 'urgency'
                  ? 'Data da Urgência: '
                  : 'Data Prevista: '}
              </h6>
              <span data-testid="expected-date-span">{getExpectedDate()}</span>
            </div>
          )}
        </S.DataOverview>
      )}
      <S.HeadingWrapper>
        <Heading
          size="large"
          color="gray"
          as="h1"
          style={{ marginBottom: '12px', marginTop: '20px' }}
        >
          Dados do paciente
        </Heading>
        {statusCanEdit() && (
          <S.Edit onClick={() => setPatientEditModal(!patientEditModal)}>
            {' '}
            Editar{' '}
          </S.Edit>
        )}
      </S.HeadingWrapper>
      <S.DataOverview>
        <div>
          <h6>Paciente: </h6>
          <span data-testid="patient-name-span">{values.patient?.name}</span>
        </div>
        <div>
          <h6>E-mail: </h6>
          <span data-testid="patient-email-span">{values.patient?.email}</span>
        </div>
        <div>
          <h6>CPF: </h6>
          <span data-testid="patient-cpf-span">{formattedCpf}</span>
        </div>
        <div>
          <h6>Data de nascimento: </h6>
          <span data-testid="patient-birthday-span">
            {values.patient?.birthday && isIsoDate(values.patient?.birthday)
              ? getDateFormattedByLocale(
                  values.patient?.birthday,
                  DateLocales.ptBr
                )
              : values.patient?.birthday}
          </span>
        </div>
        <div>
          <h6>Idade: </h6>
          <span data-testid="patient-age-span">
            {getAgeByBirthdayDate(values.patient?.birthday)}
          </span>
        </div>
        <div>
          <h6>Celular: </h6>
          <span data-testid="patient-phone-span">{values.patient?.phone}</span>
        </div>
        <div>
          <h6>Sexo: </h6>
          <span data-testid="patient-gender-span">
            {values.patient?.gender}
          </span>
        </div>
      </S.DataOverview>
      <Heading
        size="large"
        color="gray"
        as="h1"
        style={{ marginBottom: '12px', marginTop: '20px' }}
      >
        Médico e unidade
      </Heading>
      <S.DataOverview>
        <div>
          <h6>Médico: </h6>
          <span data-testid="doctor-name-span">
            {values.doctor?.name ? values.doctor?.name : 'Não informado'}
          </span>
        </div>
        <div>
          <h6>Unidade: </h6>
          <span data-testid="hospital-name-span">{values.hospital?.name}</span>
        </div>
        <div>
          <h6>CRM: </h6>
          <span data-testid="doctor-crm-span">
            {values.doctor?.crm ? values.doctor?.crm : 'Não informado'}
          </span>
        </div>
        <div>
          <h6>Bloco: </h6>
          <span data-testid="surgery-center-span">
            {values.surgery_center?.description
              ? values.surgery_center?.description
              : 'Não informado'}
          </span>
        </div>
      </S.DataOverview>
      <S.HeadingWrapper>
        <Heading
          size="large"
          color="gray"
          as="h1"
          style={{ marginBottom: '12px', marginTop: '20px' }}
        >
          Dados do convênio
        </Heading>
        {statusCanEdit() && (
          <S.Edit onClick={() => setInsuranceEditModal(!insuranceEditModal)}>
            {' '}
            Editar{' '}
          </S.Edit>
        )}
      </S.HeadingWrapper>
      <S.DataOverview>
        <div>
          <h6>Convênio: </h6>
          <span data-testid="health-insurance-name-span">
            {values.healthInsurance?.healthInsuranceName}
          </span>
        </div>
        {values.healthInsurance?.company && (
          <div>
            <h6>Empresa: </h6>
            <span data-testid="health-insurance-company-span">
              {values.healthInsurance?.company}
            </span>
          </div>
        )}
        {values.healthInsurance?.healthPlanName && (
          <div>
            <h6>Plano: </h6>
            <span data-testid="health-insurance-plan-span">
              {values.healthInsurance?.healthPlanName}
            </span>
          </div>
        )}
        {values.healthInsurance?.healthCard && (
          <div>
            <h6>N:º carteirinha: </h6>
            <span data-testid="health-insurance-card-span">
              {values.healthInsurance?.healthCard}
            </span>
          </div>
        )}
        {values.healthInsurance?.healthSubPlanName && (
          <div>
            <h6>Subplano: </h6>
            <span data-testid="health-insurance-sub-plan-span">
              {values.healthInsurance?.healthSubPlanName}
            </span>
          </div>
        )}
        {values.healthInsurance?.validThru && (
          <div>
            <h6>Validade da carteira </h6>
            <span data-testid="health-insurance-valid-thru-span">
              {getDateFormattedByLocale(
                values.healthInsurance.validThru,
                DateLocales.ptBr
              )}
            </span>
          </div>
        )}
      </S.DataOverview>

      {(values.hospital.hospital_id === HOSPITAL_SALVADOR_ID ||
        values.hospital.hospital_id === HOSPITAL_EMEC_ID) && (
        <>
          <Heading
            size="large"
            color="gray"
            as="h1"
            style={{ marginBottom: '12px', marginTop: '20px' }}
          >
            Dados da Cooperativa
          </Heading>
          <div>
            <h6>Nome da Cooperativa de cobrança do honorário médico: </h6>
            <span>{values?.cooperative_name || ''}</span>
          </div>
        </>
      )}

      <Heading
        size="large"
        color="gray"
        as="h1"
        style={{ marginBottom: '12px', marginTop: '20px' }}
      >
        Cirurgia
      </Heading>
      <S.SurgicalContainer>
        <S.SurgeryData>
          <div>
            <h6>Alergia a látex: </h6>
            <span data-testid="allergy-span">
              {values?.allergy === 'latex-not-informed'
                ? 'Alergia a látex não informada'
                : values?.allergy === 'Látex'
                ? 'Alérgico a látex'
                : 'Não alérgico a látex'}
            </span>
          </div>
          {values.otherAllergies && (
            <div>
              <h6>Outras alergias: </h6>
              <span data-testid="other-allergies-span">
                {values.otherAllergies}
              </span>
            </div>
          )}

          <div>
            <h6>Paciente oncológico ou investigação: </h6>
            <span data-testid="oncology-or-research">
              {values.oncology_or_research ? 'Sim' : 'Não'}
            </span>
          </div>
          <div>
            <h6>Banco de sangue: </h6>
            <span data-testid="extra-blood-span">
              {values.extraBlood ? 'Sim' : 'Não'}
            </span>
          </div>
          <div>
            <h6>Uso CTI: </h6>
            <span data-testid="cti-span">
              {values?.cti
                ? 'Precisa de CTI'
                : values?.cti === false
                ? 'Não precisa de CTI'
                : 'Não informado'}
            </span>
          </div>
          <div>
            <h6>Banco de sangue: </h6>
            <span data-testid="extra-blood-span">
              {values.extraBlood ? 'Sim' : 'Não'}
            </span>
          </div>
        </S.SurgeryData>

        <S.SurgeryData>
          <div>
            <h6>Técnica: </h6>
            <span data-testid="technique-span">{getTechnique()}</span>
          </div>

          <div data-testid="freeze-span">
            <h6>Congelação: </h6>
            {values?.freeze
              ? 'Sim'
              : values?.freeze === false
              ? 'Não'
              : 'Não informado'}
          </div>

          <div>
            <h6>Paciente já internado: </h6>
            <span data-testid="patient-hospitalized-span">
              {values.patientHospitalized ? 'Sim' : 'Não'}
            </span>
          </div>

          <div>
            <h6>Regime hospitalar: </h6>
            <span data-testid="hospital-regime-span">
              {getHospitalRegime()}
            </span>
          </div>

          <div>
            <h6>Diárias: </h6>
            <span data-testid="requested-dailies-span">
              {values.requestedDailies ? values.requestedDailies : 0}
            </span>
          </div>
        </S.SurgeryData>

        <S.SurgeryData>
          {profile === Profile.SECRETARY && (
            <div>
              <h6>Transfusão sanguínea: </h6>
              <span>{values.bloodTransfusion ? 'Sim' : 'Não'}</span>
            </div>
          )}
          <div>
            <h6>OPME: </h6>
            <span data-testid="opmes-span">
              {(values.opmes && values.opmes.length > 0) ||
              (values.anesthesiologies && values.anesthesiologies.length > 0)
                ? 'Sim'
                : 'Não'}
            </span>
          </div>
          {values.anesthesiologies && (
            <S.SurgicalDataAnesthesiology>
              <h6>Anestesiologia: </h6>
              {values?.anesthesiologies?.map((anesthesiology, index, array) => (
                <>
                  <span data-testid="opmes-span">{anesthesiology.name}</span>
                  {index !== array.length - 1 && <span>,</span>}
                </>
              ))}
            </S.SurgicalDataAnesthesiology>
          )}
          <div>
            <h6>Caráter: </h6>
            <span data-testid="character-span">{getCharacter()}</span>
          </div>
          <div>
            {values.hospitalizationType === 'urgency' ? (
              <div>
                <h6>Data da urgência: </h6>
                <span data-testid="expected-date-span">
                  {getExpectedDate()}
                </span>
                <SupportTextNew color="neutral700" weight="semiBold">
                  **Declaro estar ciente de que a senha será solicitada com o
                  paciente no local. Após 7 dias se procedimento não realizado o
                  pedido será automaticamente cancelado.
                </SupportTextNew>
              </div>
            ) : (
              <div>
                <SupportTextNew color="neutral700" weight="semiBold">
                  **Declaro estar ciente de que este pedido será enviado a
                  operadora e o prazo de resposta é de até 21 dias.
                </SupportTextNew>
              </div>
            )}
          </div>
        </S.SurgeryData>

        {/* TODO: Ajustar esse bloco */}
        <S.SurgicalDataOptional>
          {values.locale_hospitalization && (
            <div>
              <h6>Local da internação: </h6>
              <span data-testid="patient-hospitalized-span">
                {values?.locale_hospitalization}
              </span>
            </div>
          )}

          {values?.bullish_forecast_date && (
            <div>
              <h6>Previsão de alta: </h6>
              <span data-testid="hospital-regime-span">
                {moment.utc(values.bullish_forecast_date).format('DD/MM/YYYY')}
              </span>
            </div>
          )}
        </S.SurgicalDataOptional>
      </S.SurgicalContainer>
      <Heading
        size="large"
        color="gray"
        as="h1"
        style={{ marginBottom: '12px', marginTop: '20px' }}
      >
        Procedimentos
      </Heading>
      {values.procedures?.map((procedure) => (
        <S.ProceduresWrapper key={procedure.tuss_id}>
          <div>
            <h6>Código do procedimento: </h6>
            <span data-testid={`${procedure.tuss_id}-tuss-id-span`}>
              {procedure.tuss_id}
            </span>
          </div>
          <div>
            <h6>Descrição: </h6>
            <span data-testid={`${procedure.tuss_id}-tuss-description-span`}>
              {procedure.description}
            </span>
          </div>
          <div>
            <h6>Quantidade: </h6>
            <span data-testid={`${procedure.tuss_id}-tuss-quantity-span`}>
              {procedure.quantity}
            </span>
          </div>
        </S.ProceduresWrapper>
      ))}
      <Heading
        size="large"
        color="gray"
        as="h1"
        style={{ marginBottom: '12px', marginTop: '20px' }}
      >
        CID
      </Heading>
      {values.cids?.map((cid) => (
        <S.ProceduresWrapper key={cid.cid_id}>
          <div>
            <h6>Código do procedimento: </h6>
            <span data-testid={`${cid.cid_id}-cid-id-span`}>{cid.cid_id}</span>
          </div>
          <div>
            <h6>Descrição: </h6>
            <span data-testid={`${cid.cid_id}-cid-description-span`}>
              {cid.description}
            </span>
          </div>
        </S.ProceduresWrapper>
      ))}
      {values.opmes?.length > 0 && (
        <>
          <S.HeadingWrapper>
            <Heading
              size="large"
              color="gray"
              as="h1"
              style={{ marginBottom: '12px', marginTop: '20px' }}
            >
              OPME
            </Heading>
          </S.HeadingWrapper>
          <S.OpmeColumnsGrid>
            <S.OpmeCategoryColumn>
              <S.OpmeCategoryHeading>Gabarito</S.OpmeCategoryHeading>
              <DividerNew marginTop="8px" marginBottom="16px" />
              {categorized.gabarito.map((opme, index) => (
                <React.Fragment key={`${opme.description}-${index}`}>
                  {index > 0 && (
                    <DividerNew marginTop="16px" marginBottom="16px" />
                  )}
                  <S.OpmeItemWrapper>
                    <div>
                      <h6>OPME: </h6>
                      <span>{opme.description}</span>
                    </div>
                    <div>
                      <h6>Quantidade: </h6>
                      <span>{opme.quantity}</span>
                    </div>
                  </S.OpmeItemWrapper>
                </React.Fragment>
              ))}
            </S.OpmeCategoryColumn>
            <S.OpmeCategoryColumn>
              <S.OpmeCategoryHeading>Inclusões do médico</S.OpmeCategoryHeading>
              <DividerNew marginTop="8px" marginBottom="16px" />
              {categorized.inclusoesMedico.map((opme, index) => (
                <React.Fragment key={`${opme.description}-${index}`}>
                  {index > 0 && (
                    <DividerNew marginTop="16px" marginBottom="16px" />
                  )}
                  <S.OpmeItemWrapper>
                    <div>
                      <h6>OPME: </h6>
                      <span>{opme.description}</span>
                    </div>
                    <div>
                      <h6>Quantidade: </h6>
                      <span>{opme.quantity}</span>
                    </div>
                  </S.OpmeItemWrapper>
                </React.Fragment>
              ))}
            </S.OpmeCategoryColumn>
            <S.OpmeCategoryColumn>
              <S.OpmeCategoryHeading>
                Negado em solicitações anteriores
              </S.OpmeCategoryHeading>
              <DividerNew marginTop="8px" marginBottom="16px" />
              {categorized.negado.map((opme, index) => (
                <React.Fragment key={`${opme.description}-${index}`}>
                  {index > 0 && (
                    <DividerNew marginTop="16px" marginBottom="16px" />
                  )}
                  <S.OpmeItemWrapper denied>
                    <div>
                      <h6>OPME: </h6>
                      <span>{opme.description}</span>
                    </div>
                    <div>
                      <h6>Quantidade: </h6>
                      <span>{opme.quantity}</span>
                    </div>
                  </S.OpmeItemWrapper>
                </React.Fragment>
              ))}
              {!!values.opme_denied_reason?.trim() && (
                <S.DeniedReasonWrapper>
                  <TextAreaNew
                    label="Justificativa médica para inclusão de opme com histórico de negativa"
                    labelOverlapping
                    labelColor="danger700"
                    labelBgColor="danger50"
                    labelSize="xxxsmall"
                    labelWeight="semiBold"
                    value={values.opme_denied_reason}
                    // disabled
                    readOnly
                    outline
                    outlineColor="danger200"
                    bgColor="danger25"
                    textColor="neutral800"
                    fontSize="xsmall"
                    rows={5}
                  />
                </S.DeniedReasonWrapper>
              )}
            </S.OpmeCategoryColumn>
          </S.OpmeColumnsGrid>
          {values.providers?.filter((provider) => !!provider)?.length > 0 && (
            <>
              <Heading
                size="large"
                color="gray"
                as="h4"
                style={{ marginBottom: '12px', marginTop: '20px' }}
              >
                Fornecedores
              </Heading>
              <S.ProvidersWrapper>
                {values.providers?.map((provider, index) => (
                  <div key={index}>
                    <h6>Fornecedor {index + 1}: </h6>
                    <span>{provider}</span>
                  </div>
                ))}
              </S.ProvidersWrapper>
            </>
          )}
        </>
      )}

      <TextArea
        bgColor="mainBg"
        label="História Clínica"
        disabled
        value={values?.clinicalHistory}
        defaultValue={values?.clinicalHistory}
        rows={5}
        style={{ marginTop: '24px' }}
        data-testid="clinical-history-disabled-input"
      />
      <TextArea
        bgColor="mainBg"
        label="Observações"
        disabled
        value={values?.observations}
        defaultValue={values?.observations}
        rows={5}
        style={{ marginTop: '24px' }}
        data-testid="observations-disabled-input"
      />
      <S.DocumentsContainer>
        <DocumentsArea
          reportRecommendation={values.report_recommendation}
          defaultDocuments={documents}
          isReviewOnly
          handleDownloadDocument={handleDownloadDocumentFile}
          hasDocuments={documents?.some((document) =>
            documentList.some(
              (docList) =>
                docList.type.includes(document.type) ||
                document.type === PatientDocument.EXAM_REPORT
            )
          )}
          canSee={values.canSee}
        />
      </S.DocumentsContainer>
      {statusCanEdit() ? (
        <>
          <Button
            type="button"
            style={{ marginTop: '30px', float: 'right' }}
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
            data-testid="create-edit-surgical-button"
          >
            Salvar
          </Button>
          <Button
            type="button"
            variant="outlined"
            style={{
              marginTop: '30px',
              float: 'right',
              marginRight: 20
            }}
            onClick={goBack}
            data-testid="cancel-edit-surgical-button"
          >
            Cancelar
          </Button>
        </>
      ) : (
        <Button
          type="button"
          style={{ marginTop: '30px', float: 'right' }}
          onClick={submitForm}
          disabled={isSubmitting || isUploadingProp}
          data-testid="create-surgical-button"
        >
          {onlyVisualization ? 'Voltar' : 'Criar planejamento'}
        </Button>
      )}

      {statusCanEdit() && patientEditModal && (
        <EditPatientDataModal
          showModal={(show: boolean) => setPatientEditModal(show)}
          patientData={values.patient}
          changePatientData={(data: ModifiedPatientData) =>
            handlePatientData(data)
          }
        />
      )}

      {statusCanEdit() && insuranceEditModal && (
        <EditInsuranceModal
          showModal={(show: boolean) => setInsuranceEditModal(show)}
          insuranceData={values.healthInsurance}
          insurances={insurances}
          changeInsuranceData={(data: ModifiedInsuranceData) =>
            handleInsuranceData(data)
          }
        />
      )}

      {/* {statusCanEditOPME() && opmeEditModal && (
        <EditOpmeModal
          showModal={(show: boolean) => setOpmeEditModal(show)}
          changeData={() => setHandleSuccessModal(!handleSuccessModal)}
        />
      )} */}

      <SuccessModal
        title="Dados alterados com sucesso!"
        show={handleSuccessModal}
        close={() => {
          setHandleSuccessModal(false)
        }}
      />
    </S.Wrapper>
  )
}

export default WithLoading(SurgicalOrderReview)
